import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const SightWordBingoGame = ({ gameData, onComplete }) => {
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [markedWords, setMarkedWords] = useState([]);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [bingo, setBingo] = useState(false);

    const currentWord = gameData?.words?.[currentWordIndex];
    const gridSize = gameData?.grid_size || 3;

    useEffect(() => {
        if (markedWords.length < gridSize) return;
        const isBingo = checkBingo(markedWords, gridSize, gameData.words);
        if (isBingo && !bingo) {
            setBingo(true);
            setScore(prev => prev + 50);
            triggerHaptic([200, 100, 200, 100, 400]);
        }
    }, [markedWords, gridSize, gameData, bingo]);

    const checkBingo = (markedIds, size, words) => {
        const grid = [];
        for (let i = 0; i < size; i++) {
            const row = [];
            for (let j = 0; j < size; j++) {
                row.push(words[i * size + j]?.id);
            }
            grid.push(row);
        }
        for (let i = 0; i < size; i++) if (grid[i].every(id => markedIds.includes(id))) return true;
        for (let j = 0; j < size; j++) if (grid.every(row => markedIds.includes(row[j]))) return true;
        if (grid.every((row, i) => markedIds.includes(row[i]))) return true;
        if (grid.every((row, i) => markedIds.includes(row[size - 1 - i]))) return true;
        return false;
    };

    const handleWordClick = (word) => {
        if (markedWords.includes(word.id) || feedback) return;
        triggerHaptic(30);

        if (word.id === currentWord.id) {
            setFeedback('correct');
            const newMarked = [...markedWords, word.id];
            setMarkedWords(newMarked);
            const wordPoints = gameData.points_per_word || 10;
            const newScore = score + wordPoints;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                if (currentWordIndex < gameData.words.length - 1) {
                    setCurrentWordIndex(prev => prev + 1);
                } else {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    setTimeout(() => {
                        onComplete?.(newScore + (bingo ? 50 : 0));
                    }, 2000);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 1500);
        }
    };

    if (!gameData || !gameData.words || !currentWord) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-sky-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🎰</div>
                <h3 className="text-base md:text-3xl font-black text-sky-900 tracking-tighter uppercase">Ruošiami Bingo bilietai...</h3>
            </div>
        );
    }

    const progress = (markedWords.length / gameData.words.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-sky-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-sky-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        👀
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Žodžių <span className="text-sky-600">Bingo</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Sėkmė</p>
                        <p className="text-sm md:text-2xl font-black text-sky-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Surinkta</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{markedWords.length}/{gameData.words.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(14,165,233,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none rotate-12 text-sky-600">📖</div>
                
                <div className="w-full max-w-5xl space-y-4 md:space-y-8 relative z-10">
                    <h4 className="text-[10px] font-black text-sky-500 uppercase tracking-[0.6em] mb-4">Kas čia parašyta?</h4>
                    <div className="bg-white rounded-xl md:rounded-[3rem] p-6 md:p-12 shadow-[0_40px_80px_rgba(14,165,233,0.1)] border-4 border-sky-50 relative group overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.05)_0%,transparent_100%)] pointer-events-none" />
                        <p className="text-lg md:text-5xl font-black text-slate-900 leading-tight tracking-tighter italic font-serif relative z-10">
                            {currentWord.sentence.split('__').map((part, i) => (
                                <React.Fragment key={i}>
                                    {part}
                                    {i === 0 && (
                                        <span className="inline-block min-w-[100px] md:min-w-[200px] border-b-4 md:border-b-[12px] border-dashed border-sky-300 mx-2 md:mx-6 animate-pulse"></span>
                                    )}
                                </React.Fragment>
                            ))}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-6 flex flex-col items-center w-full max-w-4xl mx-auto">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-3 md:p-10 shadow-2xl border-white/60 bg-slate-900/90 relative overflow-hidden w-full flex flex-col items-center min-h-[300px] justify-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 opacity-[0.03] text-6xl md:text-[200px] font-black select-none pointer-events-none text-white">🎰</div>
                
                <div 
                    className="grid gap-2 md:gap-4 relative z-10 w-full" 
                    style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
                >
                    {gameData.words.map((word) => {
                        const isMarked = markedWords.includes(word.id);
                        
                        return (
                            <button
                                key={word.id}
                                onClick={() => handleWordClick(word)}
                                disabled={isMarked || !!feedback}
                                className={`
                                    relative aspect-square rounded-xl md:rounded-[3rem] flex items-center justify-center text-xs md:text-4xl font-black
                                    transition-all duration-300 transform border-b-4 md:border-b-8
                                    ${isMarked
                                        ? 'bg-emerald-500 text-white border-emerald-700 shadow-inner scale-95 border-b-0'
                                        : 'bg-white text-slate-800 border-slate-200 hover:bg-sky-50 active:translate-y-1 active:border-b-0 shadow-lg'}
                                    disabled:cursor-default
                                `}
                            >
                                <span className="relative z-10 px-2 break-words leading-none tracking-tighter uppercase italic font-serif text-center">
                                    {isMarked ? '✓' : word.word}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {bingo && (
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-600 text-white px-8 py-4 md:px-16 md:py-8 rounded-xl md:rounded-[3rem] shadow-2xl border-4 md:border-8 border-white animate-pop-in z-50 flex items-center gap-4">
                        <span className="text-2xl md:text-6xl animate-bounce">🏆</span>
                        <span className="text-2xl md:text-6xl font-black tracking-widest uppercase italic">BINGO!</span>
                        <span className="text-2xl md:text-6xl animate-bounce [animation-delay:0.2s]">🏆</span>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🎯' : '🔎'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'TIKSLU!' : 'KLAIDA!'}
                            </h2>
                            <div className={`p-4 md:p-8 rounded-2xl border-2 italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-xl font-bold text-slate-700 text-center">
                                    {feedback === 'correct' ? 'Puikiai radai trūkstamą žodį!' : 'Šis žodis čia netinka. Pabandyk dar kartą!'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SightWordBingoGame;
